import React from "react";
import styled from "@emotion/styled";
import SidePopup from "../../SidePopup/SidePopup";
import experienceIcon from "../../../Assets/Images/experience-icon.png";
import locationIcon from "../../../Assets/Images/location_icon.png";
import salaryIcon from "../../../Assets/Images/dollarIcon.png";
import jobIcon from "../../../Assets/Images/jobTypeIcon.png";
import descriptionIcon from "../../../Assets/Images/description-icon.svg";
import skillsIcon from "../../../Assets/Images/skills-icon.svg";
import tagsIcon from "../../../Assets/Images/tags-icon.svg";
import {
  applyEllipsis,
  formatDate,
} from "../../Common-Function/CommonFunctions";
import CustomTooltip from "../../Tooltip/Tooltip";

const SectionContainer = styled("div")`
  margin-bottom: 26px;
`;

const SectionHeader = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const SectionIcon = styled("img")`
  width: 22px;
  height: 22px;
`;

const SectionTitle = styled("h3")`
  margin: 0;
  color: #6860ee;
  font-size: 16px;
  font-weight: 700;
`;

const DescriptionText = styled("p")`
  margin: 0;
  color: var(--job-Description_Color);
  font-size: 14px;
  line-height: 1.7;
  white-space: pre-wrap;
  text-align: left;
`;

const PillContainer = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Pill = styled("span")`
  background-color: #efe9ff;
  color: #6f42c1;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
`;

const HeaderContainer = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  width: 100%;
`;

const HeaderTitleContainer = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const HeaderIcon = styled("img")`
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
`;

const HeaderTitle = styled("h3")`
  margin: 0;
  color: var(--header__color);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
`;

const PostedDate = styled("span")`
  color: var(--text-color-accent);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`;

const DetailsShell = styled("div")`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const MetricsContainer = styled("div")`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  background: var(--sidepopup-background);
  border: 1px solid var(--card__border);
  border-radius: 14px;
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const MetricItem = styled("div")`
  padding: 16px;
  border-right: 1px solid var(--card__border);

  &:last-child {
    border-right: none;
  }
`;

const MetricHeader = styled("div")`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const MetricIcon = styled("img")`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const MetricLabel = styled("h4")`
  margin: 0;
  color: #6860ee;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
`;

const MetricValue = styled("h3")`
  margin: 0;
  color: var(--header__color);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
`;

const ContentContainer = styled("div")`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: var(--sidepopup-background);
  border: 1px solid var(--card__border);
  border-radius: 14px;
`;


const normalizeList = (value) => {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => {
        if (typeof item === "string") return item.split(",");
        if (item?.label) return [item.label];
        if (item?.value) return [item.value];
        if (item?.name) return [item.name];
        return [];
      })
      .map((item) => String(item).trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const isValidFormattedDate = (value) => value && !value.includes("Invalid");

const hasMeaningfulNumber = (value) =>
  value !== null && value !== undefined && Number(value) !== 0;

const formatExperience = (minExperience, maxExperience) => {
  if (!hasMeaningfulNumber(minExperience)) return "N/A";

  if (hasMeaningfulNumber(maxExperience)) {
    return `${minExperience} - ${maxExperience} Years`;
  }

  return `${minExperience} Years`;
};

const formatSalary = (minSalary, maxSalary) => {
  if (!minSalary && !maxSalary) return "N/A";
  if (minSalary && maxSalary) return `${minSalary} - ${maxSalary}`;
  return minSalary || maxSalary;
};

const formatOpenings = (openings) => {
  if (!openings) return "N/A";
  return `${openings} ${Number(openings) === 1 ? "Position" : "Positions"}`;
};

const PublicJobsDetailsContainer = ({
  selectedJob,
  renderContent,
  isOpen,
  onClose,
}) => {
  const minExperience =
    selectedJob?.min_years_experience ?? selectedJob?.years_experience;
  const maxExperience = selectedJob?.max_years_experience;
  const skills = normalizeList(selectedJob?.skills);
  const tags = normalizeList(selectedJob?.tags);
  const postedDate = formatDate(selectedJob?.created_at, false, true);

  const renderJobDetailsContent = () => (
    <>
      <SectionContainer>
        <SectionHeader>
          <SectionIcon src={jobIcon} alt="Job Title" />
          <SectionTitle>Job Title</SectionTitle>
        </SectionHeader>
        <DescriptionText>
          {selectedJob?.job_role || selectedJob?.title || "N/A"}
        </DescriptionText>
      </SectionContainer>

      <SectionContainer>
        <SectionHeader>
          <SectionIcon src={descriptionIcon} alt="Description" />
          <SectionTitle>Description</SectionTitle>
        </SectionHeader>
        <DescriptionText>
          {selectedJob?.description || "No description available."}
        </DescriptionText>
      </SectionContainer>

      <SectionContainer>
        <SectionHeader>
          <SectionIcon src={skillsIcon} alt="Skills" />
          <SectionTitle>Skills</SectionTitle>
        </SectionHeader>
        {skills.length > 0 ? (
          <PillContainer>
            {skills.map((skill) => (
              <Pill key={skill}>{skill}</Pill>
            ))}
          </PillContainer>
        ) : (
          <DescriptionText>N/A</DescriptionText>
        )}
      </SectionContainer>

      <SectionContainer>
        <SectionHeader>
          <SectionIcon src={tagsIcon} alt="Tags" />
          <SectionTitle>Tags</SectionTitle>
        </SectionHeader>
        {tags.length > 0 ? (
          <PillContainer>
            {tags.map((tag) => (
              <Pill key={tag}>{tag}</Pill>
            ))}
          </PillContainer>
        ) : (
          <DescriptionText>N/A</DescriptionText>
        )}
      </SectionContainer>
    </>
  );

  const headerContent = (
    <HeaderContainer>
      <HeaderTitleContainer>
        <HeaderIcon alt="Job" src={jobIcon} />
        <HeaderTitle>
          <CustomTooltip title={selectedJob?.title} arrow>
            <span>{applyEllipsis(selectedJob?.title, 28)}</span>
          </CustomTooltip>
        </HeaderTitle>
      </HeaderTitleContainer>
      {isValidFormattedDate(postedDate) && (
        <PostedDate>Posted on : {postedDate}</PostedDate>
      )}
    </HeaderContainer>
  );

  return (
    <SidePopup
      isOpen={isOpen}
      onClose={onClose}
      showHeader={true}
      sidePopupWidth={"82%"}
      headerContent={headerContent}
      contentPadding="20px"
    >
      {selectedJob && (
        <DetailsShell>
          <MetricsContainer>
            <MetricItem>
              <MetricHeader>
                <MetricIcon src={experienceIcon} alt="Experience" />
                <MetricLabel>Experience</MetricLabel>
              </MetricHeader>
              <MetricValue>
                {formatExperience(minExperience, maxExperience)}
              </MetricValue>
            </MetricItem>
            <MetricItem>
              <MetricHeader>
                <MetricIcon src={locationIcon} alt="Location" />
                <MetricLabel>Location</MetricLabel>
              </MetricHeader>
              <MetricValue>{selectedJob.location || "N/A"}</MetricValue>
            </MetricItem>
            <MetricItem>
              <MetricHeader>
                <MetricIcon src={salaryIcon} alt="Salary" />
                <MetricLabel>Salary</MetricLabel>
              </MetricHeader>
              <MetricValue>
                {formatSalary(selectedJob?.salary_min, selectedJob?.salary_max)}
              </MetricValue>
            </MetricItem>
            <MetricItem>
              <MetricHeader>
                <MetricIcon src={jobIcon} alt="Job Type" />
                <MetricLabel>Job Type</MetricLabel>
              </MetricHeader>
              <MetricValue>{selectedJob?.job_type || "N/A"}</MetricValue>
            </MetricItem>
            <MetricItem>
              <MetricHeader>
                <MetricIcon src={jobIcon} alt="Openings" />
                <MetricLabel>Openings</MetricLabel>
              </MetricHeader>
              <MetricValue>{formatOpenings(selectedJob?.openings)}</MetricValue>
            </MetricItem>
          </MetricsContainer>

          <ContentContainer>
            {renderContent ? renderContent() : renderJobDetailsContent()}
          </ContentContainer>
        </DetailsShell>
      )}
    </SidePopup>
  );
};

export default PublicJobsDetailsContainer;
